import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Rocket, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const FILTER_API_URL = "/api/filter";
const CONTACT_API_URL = "/api/contact";
const HERO_IMAGE = "/get.webp";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  company: "",
  website: "",
  service: "",
  budget: "",
  timeline: "",
  description: "",
  goals: "",
};

const steps = [
  {
    number: "01",
    title: "Fill Out the Form",
    description: "Tell us about your project, goals, and timeline.",
  },
  {
    number: "02",
    title: "Discovery Call",
    description: "We'll schedule a call to discuss your needs in detail.",
  },
  {
    number: "03",
    title: "Proposal & Quote",
    description: "Receive a detailed proposal tailored to your project.",
  },
  {
    number: "04",
    title: "Kick Off",
    description: "Once approved, we'll start bringing your vision to life.",
  },
];

export default function GetStartedPage() {
  const [formData, setFormData] = useState(initialFormData);

  const [dropdownOptions, setDropdownOptions] = useState({
    services: [],
    budgets: [],
    timelines: [],
  });

  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getSelectedLabel = (options, selectedValue) => {
    return options.find((item) => item.value === selectedValue)?.label || "";
  };

  const fetchDropdownFilters = async () => {
    setIsLoadingFilters(true);

    try {
      const response = await fetch(FILTER_API_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to load dropdown options.");
      }

      setDropdownOptions({
        services: result?.data?.SERVICE_NEEDED || [],
        budgets: result?.data?.BUDGET_RANGE || [],
        timelines: result?.data?.TIMELINE || [],
      });
    } catch (error) {
      toast.error("Dropdown options could not be loaded.", {
        description:
          error?.message || "Please refresh the page and try again.",
      });

      setDropdownOptions({
        services: [],
        budgets: [],
        timelines: [],
      });
    } finally {
      setIsLoadingFilters(false);
    }
  };

  useEffect(() => {
    fetchDropdownFilters();
  }, []);

 const handleChange = (e) => {
  const { name, value } = e.target;

  let finalValue = value;

  
  if (name === "name") {
    finalValue = value.replace(/[^A-Za-z\s]/g, "");
  }

  setFormData((prev) => ({
    ...prev,
    [name]: finalValue,
  }));
};

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildMessage = () => {
  return formData.description.trim();
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.service || !formData.budget) {
      toast.error("Please complete all required dropdown fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        inquiryType: formData.service,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company_name: formData.company.trim(),
        message: buildMessage(),
        current_website: formData.website.trim(),
        budget_range: formData.budget,
        timeline: formData.timeline,
      };

      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let result = null;

      try {
        result = await response.json();
      } catch {
        result = null;
      }

      if (!response.ok || result?.success === false) {
        throw new Error(
          result?.message || result?.error || "Unable to submit your request."
        );
      }

      toast.success("Project request submitted!", {
        description:
          "We'll be in touch within 24 hours to schedule your discovery call.",
      });

      setFormData(initialFormData);
    } catch (error) {
      toast.error("Submission failed.", {
        description:
          error?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO
  title="Get Started | Square Trigger"
  description="Start your project with Square Trigger. Tell us about your business goals, timeline, and service needs so we can plan the right digital solution."
  ogTitle="Get Started | Square Trigger"
  ogDescription="Start your project with Square Trigger. Tell us about your business goals, timeline, and service needs so we can plan the right digital solution."
  ogSiteName="Square Trigger Get Started"
  ogImage="https://squaretrigger.com/get.webp"
  keywords={SEO_KEYWORDS?.contact || SEO_KEYWORDS?.services}
/>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="mx-auto max-w-[900px] text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-5 flex justify-center sm:mb-3"
            >
              <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                <Rocket className="h-4 w-4" />
                <span className="text-sm font-medium">Get Started</span>
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className='mx-auto mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background'
            >
              Start Your <span className="gradient-text">Digital Journey</span>
            </motion.h1>

            <motion.p
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.2 }}
  className="mx-auto mb-3 max-w-[720px] text-center text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
>
  Ready to move your business forward? Share your project details
  with us and let’s explore the right digital solution for your
  goals, workflow, and long-term growth.
</motion.p>

<motion.p
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.25 }}
  className="mx-auto mb-6 max-w-[640px] text-center text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
>
  From websites and SEO to custom tools and support, we’ll help you
  plan the next step clearly.
</motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center"
            >
              <Button
                variant="gradient"
                asChild
                className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
              >
                <a href="#project-form" className="flex items-center gap-2">
                  Start Your Project
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <Button
                variant="glass"
                asChild
                className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
              >
                <Link
                  to="/work"
                  className="flex items-center justify-center gap-2"
                >
                  View Our Work
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="relative overflow-hidden border-b border-border/70 bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              How We <span className="gradient-text">Get Started</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              A simple process helps us understand your project clearly, align
              expectations, and move into the right solution with confidence.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mb-4 text-3xl font-display font-bold gradient-text">
                  {step.number}
                </div>

                <h3 className="mb-2 text-lg font-semibold text-foreground">
                  {step.title}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section
        id="project-form"
        className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="mx-auto mb-10 max-w-[920px] text-center sm:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
                Tell Us About{" "}
                <span className="gradient-text">Your Project</span>
              </h2>

              <p className="mx-auto mt-4 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Fill out the form below and share the kind of support you need.
                The more context you provide, the more helpful our first
                conversation will be.
              </p>
            </motion.div>
          </div>

          <div className="mx-auto w-full max-w-[1540px]">
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.05 }}
              onSubmit={handleSubmit}
              className="rounded-[28px] border border-border/70 bg-card/50 px-5 py-8 shadow-soft backdrop-blur-sm sm:px-8 sm:py-10 lg:px-12 lg:py-12"
            >
              <div className="mb-8 text-center">
                <h3 className="mb-2 text-2xl font-display font-bold text-foreground">
                  Project Request Form
                </h3>

                <p className="text-muted-foreground">
                  We usually reply within 24 hours to continue the discussion.
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>

                   <Input
  id="name"
  name="name"
  value={formData.name}
  onChange={handleChange}
  placeholder="John Doe"
  required
  pattern="[A-Za-z\s]+"
  title="Please enter alphabets only"
  className="h-12"
/>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>

                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>

                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (236) 869-3335"
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name</Label>

                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Current Website (if any)</Label>

                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="h-12"
                  />
                </div>

                <div className="grid gap-6 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="service">Service Needed *</Label>

                    <Select
                      value={formData.service}
                      onValueChange={(value) =>
                        handleSelectChange("service", value)
                      }
                      disabled={isLoadingFilters}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue
                          placeholder={
                            isLoadingFilters
                              ? "Loading services..."
                              : "Select service"
                          }
                        />
                      </SelectTrigger>

                      <SelectContent>
                        {dropdownOptions.services.map((service) => (
                          <SelectItem
                            key={service.value}
                            value={service.value}
                          >
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget Range *</Label>

                    <Select
                      value={formData.budget}
                      onValueChange={(value) =>
                        handleSelectChange("budget", value)
                      }
                      disabled={isLoadingFilters}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue
                          placeholder={
                            isLoadingFilters
                              ? "Loading budgets..."
                              : "Select budget"
                          }
                        />
                      </SelectTrigger>

                      <SelectContent>
                        {dropdownOptions.budgets.map((budget) => (
                          <SelectItem key={budget.value} value={budget.value}>
                            {budget.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeline">Timeline</Label>

                    <Select
                      value={formData.timeline}
                      onValueChange={(value) =>
                        handleSelectChange("timeline", value)
                      }
                      disabled={isLoadingFilters}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue
                          placeholder={
                            isLoadingFilters
                              ? "Loading timelines..."
                              : "Select timeline"
                          }
                        />
                      </SelectTrigger>

                      <SelectContent>
                        {dropdownOptions.timelines.map((timeline) => (
                          <SelectItem
                            key={timeline.value}
                            value={timeline.value}
                          >
                            {timeline.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Project Description *</Label>

                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your project in detail. What are you looking to build? What problems are you trying to solve?"
                    rows={5}
                    required
                    className="resize-none"
                  />
                </div>

                {/* Optional field. Uncomment if needed later. */}
                {/* <div className="space-y-2">
                  <Label htmlFor="goals">Goals & Success Metrics</Label>

                  <Textarea
                    id="goals"
                    name="goals"
                    value={formData.goals}
                    onChange={handleChange}
                    placeholder="What does success look like for this project? What metrics will you use to measure it?"
                    rows={3}
                    className="resize-none"
                  />
                </div> */}

                <Button
                  type="submit"
                  variant="gradient"
                  size="xl"
                  className="group w-full justify-center"
                  disabled={isSubmitting || isLoadingFilters}
                >
                  {isSubmitting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      Submit Project Request
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>

                <p className="text-center text-sm leading-relaxed text-muted-foreground">
                  By submitting this form, you agree to our{" "}
                  <Link to="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </Layout>
  );
}